import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckIcon, 
  XMarkIcon, 
  ClockIcon, 
  UserIcon, 
  CalendarIcon,
  DocumentIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { format } from 'date-fns';
import toast from '../../utils/toast';
import PasteUploadArea from '../PasteUploadArea';
import { createVerificationRecord } from '../../utils/documentVerification';

const TaskDetails = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');

  // Modal and form states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [verificationFields, setVerificationFields] = useState({});
  const [editingVerificationField, setEditingVerificationField] = useState(null);
  const [verificationFieldValue, setVerificationFieldValue] = useState('');
  const [verificationFileUpload, setVerificationFileUpload] = useState(null);
  const [savingVerification, setSavingVerification] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    deadline: '',
    comment: ''
  });
  const [statusForm, setStatusForm] = useState({
    status: '',
    comment: ''
  });
  const [updating, setUpdating] = useState(false);

  const fetchTaskDetails = async () => {
    try {
      const [taskResponse, historyResponse, usersResponse] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/history`),
        api.get('/users')
      ]);
      setTask(taskResponse.data);
      console.log(taskResponse.data);
      setHistory(historyResponse.data);
      setUsers(usersResponse.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching task details');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {


    fetchTaskDetails();
    fetchUsers();
    fetchVerificationTypes();
  }, [taskId]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch verification types:', error);
    }
  };


  const handleGenerateInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/tasks/${taskId}/invoice`);
      toast.success('Invoice generated successfully');
      setInvoices(prev => [...prev, response.data]);
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleDownloadAttachment = async (file) => {
    try {
      const response = await api.get(file.url, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/tasks/${taskId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Create verification record for the uploaded attachment
      try {
        const verificationData = {
          documentType: 'taskAttachments',
          documentId: taskId,
          documentField: 'attachment',
          fileUrl: response.data.attachments[response.data.attachments.length - 1].url,
          fileName: file.name,
          fileSize: file.size
        };

        console.log('Verification data:', verificationData);

        const verificationResult = await createVerificationRecord(verificationData);
        
        if (verificationResult.verificationRequired) {
          toast.info('File uploaded successfully. Verification required before approval.');
        } else {
          toast.success('File uploaded successfully');
        }
      } catch (verificationError) {
        console.error('Verification error:', verificationError);
        toast.success('File uploaded successfully');
      }

      // Refresh task data to show new attachment
      const taskResponse = await api.get(`/tasks/${taskId}`);
      setTask(taskResponse.data);
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleVerificationFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVerificationFieldValue(response.data.url);
      toast.success('Verification file uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload verification file');
    }
  };

  const handleAddComment = async () => {
    try {
      await api.post(`/tasks/${taskId}/comments`, { text: comment });
      // Refresh task data to show new comment
      const response = await api.get(`/tasks/${taskId}`);
      setTask(response.data);
      setComment(''); // Clear comment input
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created':
        return '📝';
      case 'updated':
        return '✏️';
      case 'status_changed':
        return '🔄';
      case 'assigned':
        return '👥';
      case 'unassigned':
        return '👤';
      case 'file_uploaded':
        return '📎';
      case 'file_deleted':
        return '🗑️';
      case 'comment_added':
        return '💬';
      case 'comment_updated':
        return '✏️';
      case 'comment_deleted':
        return '🗑️';
      default:
        return '📌';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created':
        return 'bg-green-100 text-green-800';
      case 'updated':
        return 'bg-blue-100 text-blue-800';
      case 'status_changed':
        return 'bg-purple-100 text-purple-800';
      case 'assigned':
      case 'unassigned':
        return 'bg-yellow-100 text-yellow-800';
      case 'file_uploaded':
      case 'file_deleted':
        return 'bg-gray-100 text-gray-800';
      case 'comment_added':
      case 'comment_updated':
      case 'comment_deleted':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    console.log(changes);

    // Handle array of changes
    if (Array.isArray(changes)) {
      return changes.map((change, index) => (
        <div key={index} className="text-sm mb-1">
          <span className="font-medium">{change.field}:</span>{' '}
          <span className="line-through text-red-600">{JSON.stringify(change.oldValue)}</span>{' '}
          <span className="text-green-600">→ {JSON.stringify(change.newValue)}</span>
        </div>
      ));
    }

    // Handle single change object (for backward compatibility)
    if (typeof changes === 'object' && changes.field) {
      return (
        <div key={changes.field} className="text-sm">
          <span className="font-medium">{changes.field}:</span>{' '}
          <span className="line-through text-red-600">{JSON.stringify(changes.oldValue)}</span>{' '}
          <span className="text-green-600">→ {JSON.stringify(changes.newValue)}</span>
        </div>
      );
    }

    return null;
  };

  const handleOpenAssignmentModal = () => {
    setAssignmentForm({
      deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '',
      comment: ''
    });
    setShowAssignmentModal(true);
  };

  const handleAssignmentFormChange = (e) => {
    const { name, value } = e.target;
    setAssignmentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignmentSubmit = async () => {
    if (!assignmentForm.deadline || !assignmentForm.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUpdating(true);
    try {
      // Update task deadline only
      const updateData = {
        deadline: new Date(assignmentForm.deadline).toISOString()
      };

      await api.put(`/tasks/${taskId}`, updateData);

      // Add comment if provided
      if (assignmentForm.comment.trim()) {
        await api.post(`/tasks/${taskId}/comments`, {
          text: `Deadline Update: ${assignmentForm.comment}`
        });
      }

      // Refresh task data and history
      const [taskResponse, historyResponse] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/history`)
      ]);
      setTask(taskResponse.data);
      setHistory(historyResponse.data);

      setShowAssignmentModal(false);
      setAssignmentForm({ deadline: '', comment: '' });
      toast.success('Task deadline updated successfully');
    } catch (error) {
      toast.error('Failed to update task deadline: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenStatusModal = () => {
    setStatusForm({
      status: task.status,
      comment: ''
    });
    setShowStatusModal(true);
  };

  const handleStatusFormChange = (e) => {
    const { name, value } = e.target;
    setStatusForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusSubmit = async () => {
    if (!statusForm.status || !statusForm.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check if status is being changed to completed and task has verification types
    if (statusForm.status === 'completed' && task.verificationTypes && task.verificationTypes.length > 0) {
      // Check if all verification fields are already filled
      if (areAllVerificationFieldsFilled()) {
        // All fields are filled, proceed with status update directly
        setUpdating(true);
        try {
          // Update task status with verification data
          await api.patch(`/tasks/${taskId}/status`, {
            status: statusForm.status,
            verificationData: task.verificationData
          });

          // Add comment if provided
          if (statusForm.comment.trim()) {
            await api.post(`/tasks/${taskId}/comments`, { 
              text: `Status Update: ${statusForm.comment}` 
            });
          }

          // Refresh task data and history
          const [taskResponse, historyResponse] = await Promise.all([
            api.get(`/tasks/${taskId}`),
            api.get(`/tasks/${taskId}/history`)
          ]);

          setTask(taskResponse.data);
          setHistory(historyResponse.data);
          setShowStatusModal(false);
          setStatusForm({ status: '', comment: '' });
          toast.success('Task status updated successfully!');
        } catch (error) {
          toast.error('Failed to update task status');
        } finally {
          setUpdating(false);
        }
        return;
      }

      // Show verification modal if not all fields are filled
      setVerificationFields({});
      setShowStatusModal(false);
      setShowVerificationModal(true);
      return;
    }

    setUpdating(true);
    try {
      // Update task status
      await api.patch(`/tasks/${taskId}/status`, {
        status: statusForm.status
      });

      // Add comment if provided
      if (statusForm.comment.trim()) {
        await api.post(`/tasks/${taskId}/comments`, { 
          text: `Status Update: ${statusForm.comment}` 
        });
      }

      // Refresh task data and history
      const [taskResponse, historyResponse] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/history`)
      ]);

      setTask(taskResponse.data);
      setHistory(historyResponse.data);
      setShowStatusModal(false);
      setStatusForm({ status: '', comment: '' });
      toast.success('Task status updated successfully!');
    } catch (error) {
      toast.error('Failed to update task status');
    } finally {
      setUpdating(false);
    }
  };

  const handleVerificationSubmit = async () => {
    try {
      // Verify all required fields are filled
      const requiredFields = task.verificationTypes || [];
      console.log(requiredFields);
      console.log(verificationFields);

      const missingFields = requiredFields.filter(field => {
        const verificationType = verificationTypes.find(vt => vt._id === field.id);
        return (verificationType?.type !== 'otp-email' && !verificationFields[verificationType?.label]);
      });
      console.log(missingFields);

      if (missingFields.length > 0) {
        toast.error(`Please fill all verification fields: ${missingFields.join(', ')}`);
        return;
      }

      // Submit verification and status change
      await api.patch(`/tasks/${taskId}/status`, {
        status: 'completed',
        verificationData: verificationFields
      });

      // Add comment if provided
      if (statusForm.comment.trim()) {
        await api.post(`/tasks/${taskId}/comments`, {
          text: `Status Update: ${statusForm.comment}`
        });
      }

      // Refresh task data and history
      const [taskResponse, historyResponse] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/history`)
      ]);
      setTask(taskResponse.data);
      setHistory(historyResponse.data);

      setShowVerificationModal(false);
      setStatusForm({ status: '', comment: '' });
      setVerificationFields({});
      toast.success('Task completed successfully');
    } catch (error) {
      toast.error('Failed to complete task verification\n' + error.response?.data?.message);
      console.log(error.response?.data?.message);
    }
  };

  const sendCode = async () => {
    try {
      await api.post(`/tasks/${taskId}/generateOTP`);
      toast.success('OTP sent successfully!');
    } catch (error) {
      toast.error('Failed to send OTP');
    }
  };

  // Handle verification field editing
  const handleEditVerificationField = (fieldName, currentValue) => {
    setEditingVerificationField(fieldName);
    setVerificationFieldValue(currentValue || '');
  };

  // Handle verification field save
  const handleSaveVerificationField = async () => {
    if (!editingVerificationField) return;

    setSavingVerification(true);
    try {
      let fieldValue = verificationFieldValue;
      
      // If it's a file upload, handle the file upload first
      if (verificationFileUpload) {
        const formData = new FormData();
        formData.append('file', verificationFileUpload);
        
        console.log('Uploading file for verification field:', editingVerificationField);
        
        // Upload the file using the verification-file endpoint
        const uploadResponse = await api.post(`/tasks/${taskId}/verification-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log('Upload response:', uploadResponse.data);
        
        // Get the uploaded file URL directly from the response
        fieldValue = uploadResponse.data.url;
        console.log('editingVerificationField', fieldValue);
        // Validate the URL
        if (!fieldValue || typeof fieldValue !== 'string') {
          console.error('Invalid file URL extracted:', fieldValue);
          throw new Error('Invalid file URL received from upload');
        }
      }

      const updatedVerificationData = {
        ...task.verificationData,
        [editingVerificationField]: fieldValue
      };

      console.log('Saving verification field:', editingVerificationField, 'with value:', fieldValue);
      console.log('Updated verification data:', updatedVerificationData);

      await api.put(`/tasks/${taskId}`, {
        verificationData: updatedVerificationData
      });

      console.log('Verification data saved successfully');

      // Refresh task data from server to ensure we have the latest data
      const refreshedTaskResponse = await api.get(`/tasks/${taskId}`);
      console.log('Refreshed task data:', refreshedTaskResponse.data);
      console.log('Refreshed verification data:', refreshedTaskResponse.data.verificationData);
      setTask(refreshedTaskResponse.data);

      setEditingVerificationField(null);
      setVerificationFieldValue('');
      setVerificationFileUpload(null);
      toast.success('Verification field saved successfully!');
    } catch (error) {
      console.error('Error saving verification field:', error);
      toast.error('Failed to save verification field');
    } finally {
      setSavingVerification(false);
    }
  };

  // Handle verification field cancel
  const handleCancelVerificationField = () => {
    setEditingVerificationField(null);
    setVerificationFieldValue('');
    setVerificationFileUpload(null);
  };

  // Check if all verification fields are filled
  const areAllVerificationFieldsFilled = () => {
    if (!task.verificationTypes || task.verificationTypes.length === 0) return true;
    
    return task.verificationTypes.every(vt => {
      const verificationType = verificationTypes.find(v => v._id === vt.id);
      if (!verificationType) return true;
      
      // Check both task.verificationData and verificationFields state
      const fieldValue = task.verificationData?.[verificationType.label] || verificationFields[verificationType.label];
      return fieldValue && fieldValue.trim() !== '';
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* Task Details Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <span className={`px-3 py-1 rounded-full text-sm ${task.status === 'completed' ? 'bg-green-100 text-green-800' :
          task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
            task.status === 'cancelled' ? 'bg-red-100 text-red-800' :
              'bg-yellow-100 text-yellow-800'
          }`}>
          {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
        </span>
        <h1 className="text-2xl font-bold mb-4">{task.name}</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-gray-600 mb-2">Description</p>
            <p className="text-gray-800">{task.description}</p>
          </div>
          <div>
            {/* <p className="text-gray-600 mb-2">Status</p> */}


          </div>


        </div>
      </div>



      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6">Task Details</h2>

        <div className="grid grid-cols-2 gap-4 border rounded-lg p-4 mb-6">
          <div className="space-y-2">
            <p><span className="font-semibold">Task Name:</span> {task.name}</p>
            <div className="flex items-center ">
              <p><span className="font-semibold">Status:</span> {task.status}</p>
              <button
                onClick={handleOpenStatusModal}
                className="flex items-center px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                title="Change Status" style={{ marginLeft: '10px' }}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Change
              </button>
            </div>
            <p><span className="font-semibold">Client:</span> {task.client?.clientName}</p>
            <p><span className="font-semibold">Description:</span> {task.description}</p>
            <div>
              <p><span className="font-semibold">Assigned To:</span></p>
              {task.assignedTo && task.assignedTo.length > 0 ? (
                <div className="mt-1">
                  {task.assignedTo.map((user, index) => (
                    <span key={user._id} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2 mb-1">
                      {user.firstName} {user.lastName}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500 text-sm">No users assigned</span>
              )}
            </div>
            <div>
              <p><span className="font-semibold">Assigned Groups:</span></p>
              {task.assignedGroups && task.assignedGroups.length > 0 ? (
                <div className="mt-1">
                  {task.assignedGroups.map((group, index) => (
                    <span key={group._id} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-sm mr-2 mb-1">
                      {group.name}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500 text-sm">No groups assigned</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center ">
              <p><span className="font-semibold">Deadline:</span> {new Date(task.deadline).toLocaleString()}</p>
              <button
                onClick={handleOpenAssignmentModal}
                className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Change Deadline" style={{ marginLeft: '10px' }}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Change
              </button>
            </div>
            <p><span className="font-semibold">Created At:</span> {new Date(task.createdAt).toLocaleString()}</p>
            {task.status === 'completed' && task.verificationData && (
              <div className="mt-4">
                <p className="font-semibold"><strong>Verification Data</strong></p>
                {Object.entries(task.verificationData)
                  .filter(([key]) => key !== 'otp')
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="font-semibold">{key}:
                      {value.startsWith('http') ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                          {value}
                        </a>
                      ) : (
                        <> {value}</>
                      )}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="col-span-2">

          </div>
          <div className="col-span-2 space-y-2">


          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-3">Comments</h3>
          <div className="space-y-4">
            <div className="flex gap-2">
              <textarea
                value={comment}
                required
                onChange={(e) => setComment(e.target.value)}
                className="flex-1 p-2 border rounded"
                placeholder="Add a comment..."
                rows="3"
              />
              <button
                onClick={handleAddComment}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Comment
              </button>
            </div>
            <div className="space-y-4">
              {task.comments?.map((comment, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">{comment.text}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    By {comment.user?.firstName} {comment.user?.lastName} on {new Date(comment.createdAt).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Editable Verification Form Section - Show for tasks with verification types */}
        {task.verificationTypes && task.verificationTypes.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-3">Verification Fields</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {task.verificationTypes.map((vt) => {
                  const verificationType = verificationTypes.find(v => v._id === vt.id);
                  if (!verificationType) return null;
                  
                  const fieldName = verificationType.label;
                  const currentValue = task.verificationData?.[fieldName] || '';
                  const isEditing = editingVerificationField === fieldName;
                  
                  // Debug logging for file fields
                  if (verificationType.type === 'file') {
                    console.log(`Field: ${fieldName}, Current Value:`, currentValue, 'Type:', typeof currentValue);
                  }
                  
                  return (
                    <div key={vt.id} className="bg-white p-4 rounded border">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-700">{fieldName}</p>
                        {!isEditing && (
                          <button
                            onClick={() => handleEditVerificationField(fieldName, currentValue)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      
                      {isEditing ? (
                        <div className="space-y-2">
                          {verificationType.type === 'otp-email' ? (
                            <div>
                              <input
                                type="text"
                                value={verificationFieldValue}
                                onChange={(e) => setVerificationFieldValue(e.target.value)}
                                placeholder="Enter OTP code"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                onClick={sendCode}
                                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                              >
                                Send OTP
                              </button>
                            </div>
                          ) : verificationType.type === 'file' ? (
                            <PasteUploadArea
                              onFileSelect={handleVerificationFileUpload}
                              accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              maxSize={10 * 1024 * 1024}
                              className="mt-1"
                              dragDropText="Upload a file"
                              pasteText="or paste from clipboard (Ctrl+V)"
                            >
                              <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
                              <p className="text-sm text-gray-600">Upload a file</p>
                              <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
                            </PasteUploadArea>
                          ) : (
                            <input
                              type="text"
                              value={verificationFieldValue}
                              onChange={(e) => setVerificationFieldValue(e.target.value)}
                              placeholder={`Enter ${fieldName}`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={handleSaveVerificationField}
                              disabled={savingVerification}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {savingVerification ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelVerificationField}
                              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {currentValue ? (
                            verificationType.type === 'file' ? (
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <a
                                  href={currentValue}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  Download File
                                </a>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-800 break-all">{currentValue}</p>
                            )
                          ) : (
                            <p className="text-sm text-gray-500 italic">Not filled</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Verification Details Section - Only show for completed tasks */}
        {/* {task.status === 'completed' && task.verificationData && Object.keys(task.verificationData).filter(key => key !== 'otp').length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-3">Verification Details</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(task.verificationData)
                  .filter(([key]) => key !== 'otp')
                  .map(([key, value]) => (
                    <div key={key} className="bg-white p-3 rounded border">
                      <p className="text-sm font-medium text-gray-700 mb-1">{key}</p>
                      {typeof value === 'string' && value.startsWith('http') ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm break-all"
                        >
                          {value}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-800 break-all">{value}</p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )} */}

        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-3">Attachments</h3>
          <div className="space-y-4">
            <PasteUploadArea
              onFileSelect={handleFileUpload}
              accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              maxSize={10 * 1024 * 1024}
              pasteText="or paste from clipboard (Ctrl+V)"
            >
              <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-600">Upload a file</p>
              <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
            </PasteUploadArea>
            <div className="space-y-3">
              {task.attachments?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <p className="font-medium text-gray-800">{file.name}</p>
                      <div className="text-xs text-gray-500 space-x-2">
                        <span>Uploaded: {new Date(file.uploadedAt).toLocaleString()}</span>
                        {file.uploadedBy && (file.uploadedBy.firstName || file.uploadedBy.lastName) ? (
                          <span>by {file.uploadedBy.firstName} {file.uploadedBy.lastName}</span>
                        ) : file.uploadedBy ? (
                          <span>by {file.uploadedBy}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadAttachment(file)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Download
                  </button>
                </div>
              ))}
              {(!task.attachments || task.attachments.length === 0) && (
                <p className="text-gray-500 text-center py-4">No attachments uploaded yet.</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Invoices</h3>
            <button
              onClick={handleGenerateInvoice}
              disabled={loading}
              className={`px-4 py-2 rounded bg-blue-600 text-white ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
            >
              {loading ? 'Generating...' : 'Generate Invoice'}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice._id}>
                    <td className="px-6 py-4 whitespace-nowrap">{invoice.invoiceNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{new Date(invoice.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">${invoice.amount.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{invoice.status}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDownloadInvoice(invoice._id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>



      </div>

      {/* History Timeline Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-6">Task History</h2>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {/* History items */}
          <div className="space-y-6">
            {history.map((item, index) => (
              <div key={item._id} className="relative pl-12">
                {/* Timeline dot */}
                <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(item.action)
                  }`}>
                  {getActionIcon(item.action)}
                </div>

                {/* Content */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="font-medium">
                        {item.user.firstName} {item.user.lastName}
                      </span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-sm text-gray-500">
                        {format(new Date(item.createdAt), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getActionColor(item.action)
                      }`}>
                      {item.action.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>

                  <p className="text-gray-700 mb-2">{item.details}</p>

                  {item.changes && (
                    <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                      {formatChanges(item.changes)}
                    </div>
                  )}

                  {item.metadata && Object.keys(item.metadata).length > 0 && (
                    <div className="mt-2 text-sm text-gray-500">
                      {Object.entries(item.metadata).map(([key, value]) => (
                        <div key={key}>
                          <span className="font-medium">{key}:</span> {JSON.stringify(value)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Change Deadline</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deadline *
                </label>
                <input
                  type="date"
                  name="deadline"
                  value={assignmentForm.deadline}
                  onChange={handleAssignmentFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment (Optional)
                </label>
                <textarea
                  name="comment"
                  value={assignmentForm.comment}
                  onChange={handleAssignmentFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Add a comment about this change..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignmentSubmit}
                disabled={updating || !assignmentForm.deadline}
                className={`px-4 py-2 text-white rounded ${updating || !assignmentForm.deadline
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {updating ? 'Updating...' : 'Update Deadline'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Change Status</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  name="status"
                  value={statusForm.status}
                  onChange={handleStatusFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">Select a status</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment *
                </label>
                <textarea
                  name="comment"
                  value={statusForm.comment}
                  onChange={handleStatusFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="3"
                  placeholder="Add a comment about this status change..."
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleStatusSubmit}
                disabled={updating || !statusForm.status || !statusForm.comment}
                className={`px-4 py-2 text-white rounded ${updating || !statusForm.status || !statusForm.comment
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
                  }`}
              >
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && task && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Task Verification</h2>

            {task.verificationTypes?.map(type => {
              const verificationType = verificationTypes.find(vt => vt._id === type.id);
              return verificationType ? (
                <div key={type.id} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">{verificationType.label}</label>
                  {verificationType.type === 'otp-email' ? (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        maxLength={6}
                        pattern="\d{6}"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        onChange={e => {
                          const value = e.target.value;
                          console.log(value);
                          setVerificationFields(prev => ({
                            ...prev,
                            ["otp"]: value
                          }));
                        }}
                        placeholder="Enter 6 digit OTP"
                      />
                      <button
                        className="mt-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={sendCode}
                      >
                        Resend
                      </button>
                    </div>
                  ) : verificationType.type === 'file' ? (
                    <PasteUploadArea
                      onFileSelect={handleVerificationFileUpload}
                      accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      maxSize={10 * 1024 * 1024}
                      className="mt-1"
                      dragDropText="Upload a file"
                      pasteText="or paste from clipboard (Ctrl+V)"
                    >
                      <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600">Upload a file</p>
                      <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
                    </PasteUploadArea>
                  ) : (
                    <input
                      type={verificationType.type || 'text'}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                      value={verificationFields[verificationType.label] || ''}
                      onChange={e => setVerificationFields(prev => ({
                        ...prev,
                        [verificationType.label]: e.target.value
                      }))}
                    />
                  )}
                </div>
              ) : null;
            })}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                onClick={() => {
                  setShowVerificationModal(false);
                  setVerificationFields({});
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleVerificationSubmit}
              >
                Verify & Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetails;